// FAQ / HowTo 自定义内容的字段定义、校验和草稿结构。
// 保存结果先留在前端，接口对接时直接使用 toSchemaEditorPayload。

export const SCHEMA_EDITOR_TYPE = {
    faq: 'faq',
    howto: 'howto',
};

export const SCHEMA_KEY_EDITOR_TYPE = {
    has_faq: SCHEMA_EDITOR_TYPE.faq,
    has_how_to: SCHEMA_EDITOR_TYPE.howto,
};

// schemaKey -> 后端 structured_data 里的内容字段（回填用）
export const SCHEMA_KEY_CONTENT_FIELD = {
    has_faq: 'faq_content',
    has_how_to: 'how_to_content',
};

const FAQ_MAX_ITEMS = 10;
const HOWTO_MAX_STEPS = 20;
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 500;

const faqItemFields = [
    {
        key: 'question',
        label: 'Question',
        placeholder: 'Add a value',
        required: true,
        maxLength: TITLE_MAX,
    },
    {
        key: 'answer',
        label: 'Answer',
        placeholder: 'Add a value',
        required: true,
        // 本版先不限制 Answer 长度
        // maxLength: DESCRIPTION_MAX,
    },
];

const howtoMetaFields = [
    {
        key: 'title',
        label: 'Title',
        placeholder: 'e.g. How to make coffee',
        required: true,
        maxLength: TITLE_MAX,
    },
    {
        key: 'description',
        label: 'Description',
        placeholder: 'e.g. A simple guide to making coffee at home',
        required: true,
        // 本版先不限制 Description 长度
        // maxLength: DESCRIPTION_MAX,
    },
];

const howtoStepFields = [
    {
        key: 'title',
        label: 'Step title',
        placeholder: 'Enter a short name for this step',
        required: true,
        maxLength: TITLE_MAX,
    },
    {
        key: 'description',
        label: 'Step description',
        placeholder: 'Describe what to do in this step',
        required: true,
        // 本版先不限制 Step description 长度
        // maxLength: DESCRIPTION_MAX,
    },
];

export const SCHEMA_EDITOR_CONFIG = {
    [SCHEMA_EDITOR_TYPE.faq]: {
        modalTitle: 'FAQ Schema',
        sectionTitle: 'FAQs',
        addLabel: 'Add FAQ',
        limitTip: 'You can add up to 10 FAQs.',
        emptyTip: 'Add at least one FAQ.',
        emptyTitle: 'No FAQs yet',
        emptyDesc: 'Add FAQ schema to improve AI search visibility.',
        maxItems: FAQ_MAX_ITEMS,
        itemFields: faqItemFields,
    },
    [SCHEMA_EDITOR_TYPE.howto]: {
        modalTitle: 'HowTo Schema',
        sectionTitle: 'HowTo Schema',
        listTitle: 'Steps',
        addLabel: 'Add step',
        limitTip: 'You can add up to 20 steps.',
        emptyTip: 'Add at least one step.',
        emptyTitle: 'No steps added yet',
        emptyDesc: 'Add HowTo schema to improve AI search visibility.',
        maxItems: HOWTO_MAX_STEPS,
        metaFields: howtoMetaFields,
        itemFields: howtoStepFields,
    },
};

let schemaEditorIdSeed = 0;

export function createSchemaEditorId(prefix) {
    schemaEditorIdSeed += 1;
    return `${prefix}_${Date.now().toString(36)}_${schemaEditorIdSeed}`;
}

// 按 Unicode 码点计数，避免 emoji 和增补字符被拆成两个字符。
export function countSchemaChars(value) {
    return Array.from(value || '').length;
}

export function getSchemaListKey(editorType) {
    return editorType === SCHEMA_EDITOR_TYPE.howto ? 'steps' : 'items';
}

export function createEmptySchemaItem(editorType) {
    const id = createSchemaEditorId(editorType === SCHEMA_EDITOR_TYPE.howto ? 'step' : 'faq');
    if (editorType === SCHEMA_EDITOR_TYPE.howto) {
        return { id, title: '', description: '' };
    }
    return { id, question: '', answer: '' };
}

export function normalizeSchemaContent(editorType, content) {
    if (editorType === SCHEMA_EDITOR_TYPE.howto) {
        const source = content || {};
        return {
            title: source.title || '',
            description: source.description || '',
            steps: (source.steps || []).map((step) => ({
                id: step.id || createSchemaEditorId('step'),
                title: step.title || '',
                description: step.description || '',
            })),
        };
    }

    const source = content || {};
    return {
        items: (source.items || []).map((item) => ({
            id: item.id || createSchemaEditorId('faq'),
            question: item.question || '',
            answer: item.answer || '',
        })),
    };
}

export function isSchemaItemValid(editorType, item) {
    if (!item) return false;
    return SCHEMA_EDITOR_CONFIG[editorType].itemFields.every((field) => !getSchemaFieldError(field, item[field.key]));
}

export function getSchemaFieldError(field, value) {
    const text = value == null ? '' : String(value);
    if (field.maxLength && countSchemaChars(text) > field.maxLength) {
        return {
            code: 'max',
            key: '{{v1}} must be {{v2}} characters or fewer.',
            label: field.label,
            maxLength: field.maxLength,
        };
    }
    if (field.required && text.trim() === '') {
        return {
            code: 'required',
            key: '{{v1}} is required.',
            label: field.label,
        };
    }
    return null;
}

function collectItemErrors(fields, item) {
    const errors = {};
    fields.forEach((field) => {
        const error = getSchemaFieldError(field, item[field.key]);
        if (error) errors[field.key] = error;
    });
    return errors;
}

export function validateSchemaContent(editorType, draft) {
    const config = SCHEMA_EDITOR_CONFIG[editorType];
    const listKey = getSchemaListKey(editorType);
    const items = draft[listKey] || [];

    // 空列表视为“清空”，直接通过校验（对应接口 faq:[] / howto:[]，见文档 3.4）
    if (items.length === 0) {
        return {
            ok: true,
            metaErrors: {},
            itemErrors: {},
            invalidIds: [],
            firstInvalidItemId: null,
            listError: null,
        };
    }

    const metaErrors = {};
    const itemErrors = {};
    const invalidIds = [];

    (config.metaFields || []).forEach((field) => {
        const error = getSchemaFieldError(field, draft[field.key]);
        if (error) metaErrors[field.key] = error;
    });

    items.forEach((item) => {
        const errors = collectItemErrors(config.itemFields, item);
        if (Object.keys(errors).length) {
            itemErrors[item.id] = errors;
            invalidIds.push(item.id);
        }
    });

    let listError = null;
    if (items.length > config.maxItems) {
        listError = config.limitTip;
    }

    return {
        ok: !listError && !invalidIds.length && !Object.keys(metaErrors).length,
        metaErrors,
        itemErrors,
        invalidIds,
        firstInvalidItemId: invalidIds[0] || null,
        listError,
    };
}

// 后端 structured_data.faq_content / how_to_content 可能是 JSON 字符串或已解析的数组。
export function parseSchemaContentString(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== 'string') return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

// 后端内容（faq_content / how_to_content）-> 编辑器可用的 content（交给 normalizeSchemaContent 补 id）。
// FAQ：question/answer 直接对应；HowTo：name->title、description->description、steps[].name->title、steps[].text->description。
// 编辑器只支持单个 HowTo，取数组第一项。
export function fromSchemaApiContent(editorType, raw) {
    const list = parseSchemaContentString(raw);
    if (editorType === SCHEMA_EDITOR_TYPE.howto) {
        const first = list[0] || {};
        return {
            title: first.name || '',
            description: first.description || '',
            steps: (first.steps || []).map((step) => ({
                title: step.name || '',
                description: step.text || '',
            })),
        };
    }
    return {
        items: list.map((item) => ({
            question: item.question || '',
            answer: item.answer || '',
        })),
    };
}

// 编辑器 payload（toSchemaEditorPayload 的产物）-> 后端接口 body。
// 接口字段见文档 section 3：FAQ 用 question/answer；HowTo 用 name/description + steps[].name/text。
// 编辑器只支持单个 HowTo，这里包成单元素数组以匹配接口的数组结构。
export function toSchemaApiBody(payload) {
    if (!payload) return {};
    if (payload.type === SCHEMA_EDITOR_TYPE.howto) {
        const steps = (payload.steps || []).map((step) => ({
            name: step.title || '',
            text: step.description || '',
        }));
        // 没有步骤视为清空
        if (steps.length === 0) {
            return { howto: [] };
        }
        return {
            howto: [
                {
                    name: payload.title || '',
                    description: payload.description || '',
                    steps,
                },
            ],
        };
    }
    return {
        faq: (payload.items || []).map((item) => ({
            question: item.question || '',
            answer: item.answer || '',
        })),
    };
}

export function toSchemaEditorPayload(editorType, draft) {
    const listKey = getSchemaListKey(editorType);
    const items = (draft[listKey] || []).map((item, index) => {
        const next = {
            id: item.id,
            position: index + 1,
        };
        SCHEMA_EDITOR_CONFIG[editorType].itemFields.forEach((field) => {
            next[field.key] = String(item[field.key] || '').trim();
        });
        return next;
    });

    if (editorType === SCHEMA_EDITOR_TYPE.howto) {
        return {
            type: SCHEMA_EDITOR_TYPE.howto,
            title: String(draft.title || '').trim(),
            description: String(draft.description || '').trim(),
            steps: items,
        };
    }

    return {
        type: SCHEMA_EDITOR_TYPE.faq,
        items,
    };
}
